import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  rowsPerPage: number;
  handlePageChange: (page: number) => void;
  hasHidePagination?: boolean;
}

const Pagination = ({
  currentPage,
  totalPages,
  totalRecords,
  rowsPerPage,
  handlePageChange,
  hasHidePagination,
}: PaginationProps) => {
  const startRecord = (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, totalRecords);

  const handleSelectChange = (e: SelectChangeEvent<number>) => {
    handlePageChange(Number(e.target.value));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        py: 1.5,
        px: 2,
      }}
    >
      {!hasHidePagination && (
        <Typography
          sx={{
            fontSize: '0.8125rem',
            color: 'text.secondary',
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          Showing{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {startRecord}
          </Box>{' '}
          –{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {endRecord}
          </Box>{' '}
          of{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {totalRecords}
          </Box>{' '}
          records
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            p: 0.5,
            borderRadius: '6px',
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Previous */}
          <IconButton
            size="small"
            disabled={currentPage <= 1}
            onClick={() => handlePageChange(currentPage - 1)}
            aria-label="Previous page"
            sx={{
              width: 30,
              height: 30,
              borderRadius: '4px',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton>

          {/* Page Select */}
          <FormControl size="small">
            <Select
              value={currentPage}
              onChange={handleSelectChange}
              sx={{
                height: 30,
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: 'background.paper',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1.5,
                },
              }}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <MenuItem key={page} value={page} sx={{ fontSize: '0.75rem' }}>
                  Page {page}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Next */}
          <IconButton
            size="small"
            disabled={currentPage >= totalPages || totalPages === 0}
            onClick={() => handlePageChange(currentPage + 1)}
            aria-label="Next page"
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {totalPages} Pages
        </Typography>
      </Box>
    </Box>
  );
};

export default Pagination;
